import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Zap, Wind, Shield, Heart, Gauge } from 'lucide-react';
import { useGameStore } from '@/game';
interface GameHUDProps {
  onPause: () => void;
}
export function GameHUD({ onPause }: GameHUDProps) {
  const score = useGameStore((s) => s.score);
  const highScore = useGameStore((s) => s.highScore);
  const speed = useGameStore((s) => s.speed);
  const lives = useGameStore((s) => s.lives);
  const powerUps = useGameStore((s) => s.powerUps);
  const timers = useGameStore((s) => s.powerUpTimers);
  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      {/* Top bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start gap-3"
      >
        <motion.div
          key={`score-${score}`}
          initial={{ scale: 1 }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.2 }}
          className="bg-black/65 backdrop-blur-md rounded-2xl px-5 py-3 text-white shadow-lg ring-1 ring-white/10"
        >
          <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold">
            Score
          </div>
          <div className="text-2xl font-black tabular-nums leading-tight">
            {score.toLocaleString()}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="bg-black/65 backdrop-blur-md rounded-2xl px-4 py-3 text-white text-center shadow-lg ring-1 ring-white/10"
        >
          <div className="text-[10px] text-yellow-300 uppercase tracking-widest font-bold">
            Best
          </div>
          <div className="text-xl font-black tabular-nums leading-tight text-yellow-400">
            {highScore.toLocaleString()}
          </div>
        </motion.div>
        <motion.button
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.05 }}
          onClick={onPause}
          className="pointer-events-auto bg-black/65 backdrop-blur-md hover:bg-black/80 active:bg-black/90 rounded-2xl p-4 text-white shadow-lg ring-1 ring-white/10 min-w-[56px] min-h-[56px] flex items-center justify-center transition-colors"
          aria-label="Pause game"
        >
          <Pause className="w-7 h-7" strokeWidth={2.5} />
        </motion.button>
      </motion.div>
      {/* Side stats (speed + lives) */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="absolute top-32 right-4 flex flex-col gap-2"
      >
        <StatBadge
          icon={<Gauge className="w-4 h-4" strokeWidth={2.5} />}
          label="Speed"
          value={`${speed.toFixed(1)}x`}
          valueColor="text-green-400"
        />
        <StatBadge
          icon={<Heart className="w-4 h-4" strokeWidth={2.5} />}
          label="Lives"
          value={'♥'.repeat(Math.max(0, Math.min(5, lives)))}
          valueColor="text-red-400"
        />
      </motion.div>
      {/* Power-up pills */}
      <AnimatePresence>
        {(powerUps.speed || powerUps.fly || powerUps.shield) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-28 left-1/2 -translate-x-1/2 flex gap-2 px-4"
          >
            {powerUps.speed && (
              <PowerUpPill
                key="speed"
                icon={<Zap className="w-4 h-4" strokeWidth={3} />}
                label="Speed"
                timer={timers.speed}
                maxTimer={5000}
                color="bg-green-400"
                ringColor="ring-green-300"
              />
            )}
            {powerUps.fly && (
              <PowerUpPill
                key="fly"
                icon={<Wind className="w-4 h-4" strokeWidth={3} />}
                label="Fly"
                timer={timers.fly}
                maxTimer={5000}
                color="bg-sky-400"
                ringColor="ring-sky-300"
              />
            )}
            {powerUps.shield && (
              <PowerUpPill
                key="shield"
                icon={<Shield className="w-4 h-4" strokeWidth={3} />}
                label="Shield"
                timer={timers.shield}
                maxTimer={5000}
                color="bg-yellow-400"
                ringColor="ring-yellow-300"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
function StatBadge({
  icon,
  label,
  value,
  valueColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor: string;
}) {
  return (
    <div className="bg-black/65 backdrop-blur-md rounded-2xl px-3 py-2 text-white shadow-lg ring-1 ring-white/10 min-w-[88px]">
      <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`text-lg font-black tabular-nums leading-tight ${valueColor}`}>
        {value}
      </div>
    </div>
  );
}
function PowerUpPill({
  icon,
  label,
  timer,
  maxTimer,
  color,
  ringColor,
}: {
  icon: React.ReactNode;
  label: string;
  timer: number;
  maxTimer: number;
  color: string;
  ringColor: string;
}) {
  const pct = Math.max(0, Math.min(100, (timer / maxTimer) * 100));
  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={`bg-black/75 backdrop-blur-md rounded-full pl-3 pr-4 py-2 text-white flex items-center gap-2 shadow-lg ring-2 ${ringColor}`}
    >
      <span className={`${color} text-black rounded-full w-6 h-6 flex items-center justify-center`}>
        {icon}
      </span>
      <span className="font-black text-sm whitespace-nowrap uppercase tracking-wider">
        {label}
      </span>
      <div className="w-14 h-2 bg-white/20 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${color}`}
          style={{ width: `${pct}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>
    </motion.div>
  );
}